// 带类型化错误 + 认证失败自动重定向的轻量 fetch 包装器。

import { useAuth } from '@/store/auth.ts';

export const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// 客户端视为"会话不再有效"的服务器错误码——
// 仅真正的会话失败，重新登录可修复：
// - 1001 UNAUTHORIZED：cookie 缺失
// - 1003 TOKEN_EXPIRED：cookie 过期
// 1004 FORBIDDEN 故意不在此列：它表示"已登录，但不允许访问
// 此资源"——例如 TEACHER 从其被允许进入的管理页面命中
// 仅 ADMIN 的端点如 /api/admin/teachers（ADR-018）。重新登录
// 不会改变角色，因此重定向只会将用户踢出其本应所在的页面。
// 调用方改为内联处理（react-query error → 空/禁用 UI）；
// 路由级守卫处理真正的角色不匹配。1005 ORIGIN_NOT_ALLOWED
// 同样排除——重新登录无法修复。
const AUTH_FAIL_CODES = new Set([1001, 1003]);

// BASE_URL carries the Vite `base`, so the login path stays correct whether the
// app is mounted at / (default) or under a path prefix on a single domain.
const LOGIN_PATH = `${import.meta.env.BASE_URL}login`;

function isOnLoginPage(): boolean {
  return window.location.pathname === LOGIN_PATH;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
    credentials: 'include',
  });
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => null)) as {
    code?: number;
    message?: string;
    details?: unknown;
  } | null;

  if (!res.ok) {
    if (AUTH_FAIL_CODES.has(body?.code ?? -1) && !isOnLoginPage()) {
      useAuth.getState().clear();
      // Replace (not push) so the broken page isn't in history.
      window.location.replace(LOGIN_PATH);
    }
    // 永远不要抛出空消息——空白错误会让上层 message.error 弹出无文字的红色提示。
    // 优先后端 message（非空白），再退回 statusText，最后兜底一条带状态码的可读文案。
    const fallback = res.statusText || `Request failed (${res.status})`;
    const message = body?.message && body.message.trim() ? body.message : fallback;
    throw new ApiError(res.status, body?.code ?? 9001, message, body?.details);
  }
  return body as T;
}
